import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search, X, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Minus,
  Phone, Mail, UserCheck, Sparkles, ShoppingBag, RefreshCw, AlertTriangle,
  CalendarClock, StickyNote
} from 'lucide-react';
import supabaseService from '../services/supabaseService';
import '../assets/styles/clients.css';

// A fortnight at a time, paged with arrows — the same window and the same
// pattern as the public booking form's "Pick a time".
const REBOOK_DAYS_PER_PAGE = 14;
const SEARCH_DEBOUNCE_MS = 300;

/* ---------------------------------------------------------------- formatting */

// Local date parts, never toISOString(). NZ is UTC+12/13, so a UTC date string
// is yesterday's date for the whole first half of Viktoria's working day.
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d, n) => {
  const copy = new Date(d.getTime());
  copy.setDate(copy.getDate() + n);
  return copy;
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d
    .toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    .replace(/,/g, '');
};

// "Wednesday 12 August" — the booking form's day heading. Inside a fortnight
// the year and the abbreviations are noise.
const formatDayLabel = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' }).replace(/,/g, '');
};

const formatDateShort = (value) => {
  const d = parseDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/,/g, '');
};

const formatTime = (value) => {
  if (!value) return '';
  const [h, m] = String(value).split(':');
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return String(value);
  return `${hour % 12 || 12}:${(m || '00').slice(0, 2)}${hour >= 12 ? 'pm' : 'am'}`;
};

const formatMoney = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const whole = n % 100 === 0;
  return (n / 100).toLocaleString('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
};

// Treatment prices are stored as display strings ("$85", "POA"), so anything
// that isn't a number is shown exactly as it was entered.
const priceToCents = (price) => {
  const n = parseFloat(String(price ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

const formatPrice = (price) => {
  const cents = priceToCents(price);
  return cents === null ? (price || '—') : formatMoney(cents);
};

// Mirrors recalculate_booking_totals(): unpriced ("POA") treatments contribute
// nothing, whole dollars stay whole, and a zero total reads as POA rather than $0.
const treatmentsPriceLabel = (treatments) => {
  const cents = treatments.reduce((sum, t) => sum + (priceToCents(t.price) || 0), 0);
  if (cents <= 0) return 'POA';
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
};

const formatDuration = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const timeToMinutes = (value) => {
  const [h, m] = String(value || '').split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : null;
};

const minutesToTime = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

// treatment_cents and product_cents are maintained by database triggers; adding
// the two subtotals is the only arithmetic here. The one thing worth care is
// POA: zero cents on a consultation-priced visit is not $0, and this is the exact
// card she opens to explain a price.
const visitMoney = (visit) => {
  const products = Number(visit.product_cents) || 0;
  const rawKnown = visit.treatment_cents_raw !== null && visit.treatment_cents_raw !== undefined;
  // A booking that lost its treatment rows keeps its original price text, and
  // that string is then the only surviving record of what it cost.
  const treatments = rawKnown ? Number(visit.treatment_cents) || 0 : priceToCents(visit.total_price);
  const poa = visit.is_poa === true || treatments === null;

  return {
    poa,
    products,
    treatmentsLabel: poa ? 'POA' : formatMoney(treatments),
    totalLabel: poa
      ? products > 0
        ? `POA + ${formatMoney(products)}`
        : 'POA'
      : formatMoney(treatments + products),
  };
};

const bookingDuration = (booking) => {
  const stated = Number(booking.total_duration);
  if (Number.isFinite(stated) && stated > 0) return stated;
  const summed = (booking.booking_treatments || []).reduce(
    (sum, t) => sum + (Number(t.duration_minutes) || 0),
    0
  );
  return summed > 0 ? summed : 60;
};

// Mirrors recalculate_booking_totals(): total_duration becomes the sum of the
// treatment rows, and only keeps its old value when that sum is zero.
const projectedDuration = (visit, extraTreatment) => {
  const summed =
    (visit.treatments || []).reduce((sum, t) => sum + (Number(t.duration_minutes) || 0), 0) +
    (Number(extraTreatment.duration_minutes) || 0);
  return summed > 0 ? summed : Number(visit.total_duration) || 0;
};

// get_available_slots sizes busy blocks from total_duration, so lengthening a
// future booking can silently swallow the next appointment. She is allowed to do
// it — she may have genuinely run over — but not by accident.
const overlapWarning = (visit, treatment, dayBookings) => {
  const start = timeToMinutes(visit.time);
  const duration = projectedDuration(visit, treatment);
  if (start === null || duration <= 0) return '';
  const end = start + duration;

  const clashes = (dayBookings || [])
    .filter((b) => b.id !== visit.id && b.status === 'approved' && b.date === visit.date && b.time)
    .map((b) => ({ name: b.name, start: timeToMinutes(b.time), duration: bookingDuration(b) }))
    .filter((b) => b.start !== null && b.start < end && b.start + b.duration > start)
    .sort((a, b) => a.start - b.start);

  if (clashes.length === 0) return '';

  const who = clashes
    .map((c) => `${c.name || 'another client'} at ${formatTime(minutesToTime(c.start))}`)
    .join(', ');
  return `Adding ${treatment.name} runs this appointment to ${formatTime(minutesToTime(end))}, which overlaps ${who}.`;
};

const statusMeta = (status, date) => {
  const today = toISODate(new Date());
  switch (status) {
    case 'approved':
      return date && date >= today
        ? { cls: 'approved', label: 'Booked' }
        : { cls: 'approved', label: 'Completed' };
    case 'pending':
      return { cls: 'pending', label: 'Pending' };
    case 'rescheduled':
      return { cls: 'rescheduled', label: 'Rescheduled' };
    case 'cancelled':
      return { cls: 'declined', label: 'Cancelled' };
    case 'declined':
      return { cls: 'declined', label: 'Declined' };
    default:
      return { cls: 'declined', label: status || 'Unknown' };
  }
};

// visits counts completed appointments; total_bookings counts everything on
// file. A client can now be found with visits: 0 — pending or cancelled only.
const clientSummaryLine = (row) => {
  const visits = Number(row.visits) || 0;
  const total = Number(row.total_bookings) || 0;
  if (visits > 0) {
    return `${visits} visit${visits === 1 ? '' : 's'}${row.last_visit ? ` · ${formatDateShort(row.last_visit)}` : ''}`;
  }
  if (total > 0) return `No visits yet · ${total} booking${total === 1 ? '' : 's'} on file`;
  return 'Nothing on file yet';
};

const SOURCE_LABELS = {
  online_self: 'Booked online',
  admin_rebook: 'Rebooked in person',
  admin_manual: 'Phone or text',
};

/* ---------------------------------------------------------------- component */

// A client with no email address is keyed on their name instead -- see the
// client key in 20260824020000. The key is an opaque identity token: fine to
// hand back to get_client_history, never an address. Passing one to rebookClient
// would store "name:sarah brown" in bookings.email, which then becomes a real
// email key and splits the client in two the next time she is looked up.
const NAME_KEY_PREFIX = 'name:';
const isNameKey = (key) => typeof key === 'string' && key.startsWith(NAME_KEY_PREFIX);
// The address to actually use, or null when there is not one.
const realEmail = (key) => (isNameKey(key) ? null : key || null);

const ClientsPanel = ({ showToast }) => {
  const notify = (type, message) => {
    if (typeof showToast === 'function') showToast(type, message);
  };

  /* list */
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  /* detail */
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [optOut, setOptOut] = useState(false);
  const [optOutBusy, setOptOutBusy] = useState(false);
  const [optOutError, setOptOutError] = useState('');
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [busy, setBusy] = useState(false);

  /* catalog (treatments + shop products), fetched once, on demand */
  const [catalog, setCatalog] = useState({ treatments: { all: [], grouped: {} }, products: [] });
  const catalogRequest = useRef(null);

  /* pickers */
  const [picker, setPicker] = useState(null); // { kind, visit, loading, checking, error }
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerProduct, setPickerProduct] = useState(null);
  const [pickerQty, setPickerQty] = useState(1);
  const [pickerConfirm, setPickerConfirm] = useState(null); // { treatment, message }

  /* rebook */
  const [rebookVisit, setRebookVisit] = useState(null);
  const [rebookLoading, setRebookLoading] = useState(false);
  const [rebookChosenTreatments, setRebookChosenTreatments] = useState([]);
  const [rebookEditingTreatments, setRebookEditingTreatments] = useState(false);
  const [rebookDropped, setRebookDropped] = useState([]);
  const [rebookSlots, setRebookSlots] = useState([]);
  const [rebookSlotsState, setRebookSlotsState] = useState('idle'); // idle | loading | ready | error
  const [rebookSlotsError, setRebookSlotsError] = useState('');
  const [rebookPageStart, setRebookPageStart] = useState(() => toISODate(new Date()));
  const [rebookOpenDay, setRebookOpenDay] = useState('');
  const [rebookSlot, setRebookSlot] = useState(null);
  const [rebookNotes, setRebookNotes] = useState('');
  const [rebookInPerson, setRebookInPerson] = useState(true);
  const [rebookSubmitting, setRebookSubmitting] = useState(false);
  const [slotsReloadKey, setSlotsReloadKey] = useState(0);

  const searchSeq = useRef(0);

  /* ------------------------------------------------------------ data loading */

  useEffect(() => {
    const seq = ++searchSeq.current;
    setListLoading(true);
    const trimmed = query.trim();
    const timer = setTimeout(() => {
      supabaseService
        .searchClients(trimmed)
        .then((rows) => {
          if (seq !== searchSeq.current) return;
          setClients(rows);
          setListError('');
        })
        .catch((err) => {
          if (seq !== searchSeq.current) return;
          setClients([]);
          setListError(err.message || 'Could not load your clients.');
        })
        .finally(() => {
          if (seq === searchSeq.current) setListLoading(false);
        });
    }, trimmed ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [query]);

  const ensureCatalog = () => {
    if (!catalogRequest.current) {
      catalogRequest.current = Promise.all([
        supabaseService.getTreatments(),
        supabaseService.getAllShopProducts(),
      ])
        .then(([treatments, products]) => {
          const data = { treatments, products: products || [] };
          setCatalog(data);
          return data;
        })
        .catch((err) => {
          catalogRequest.current = null;
          throw err;
        });
    }
    return catalogRequest.current;
  };

  const loadHistory = async (email, quiet = false) => {
    if (!quiet) {
      setHistoryLoading(true);
      setHistoryError('');
    }
    try {
      const rows = await supabaseService.getClientHistory(email);
      setHistory(rows || []);
      setHistoryError('');
    } catch (err) {
      setHistory([]);
      setHistoryError(err.message || 'Could not load this client’s visits.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Totals and visit counts live on the client summary row, so it has to be
  // re-read after an edit or the header keeps showing the old lifetime spend.
  const refreshClient = async (email) => {
    try {
      const rows = await supabaseService.searchClients(email);
      const match = (rows || []).find((r) => r.email === email);
      if (match) setClient(match);
    } catch {
      /* header stays stale — not worth interrupting her for */
    }
  };

  // Only clients with an account can be opted out -- a guest has no profile row
  // to hold the flag, and no birthday saved to trigger the email either.
  const loadOptOut = async (userId) => {
    setOptOutError('');
    if (!userId) {
      setOptOut(false);
      return;
    }
    try {
      setOptOut(await supabaseService.getBirthdayOptOut(userId));
    } catch {
      // Not worth blocking the client record over. The toggle shows its real
      // state or nothing; it never guesses.
      setOptOut(false);
      setOptOutError('Could not read their birthday email setting.');
    }
  };

  const toggleOptOut = async (next) => {
    if (!client?.user_id || optOutBusy) return;
    setOptOutBusy(true);
    setOptOutError('');
    const previous = optOut;
    setOptOut(next);
    try {
      setOptOut(await supabaseService.setBirthdayOptOut(client.user_id, next));
    } catch (err) {
      setOptOut(previous);
      setOptOutError(err.message || 'That did not save.');
    } finally {
      setOptOutBusy(false);
    }
  };

  const openClient = (row) => {
    setClient(row);
    setHistory([]);
    setExpandedVisit(null);
    setPendingRemoval(null);
    loadHistory(row.email);
    loadOptOut(row.user_id);
  };

  const closeClient = () => {
    setClient(null);
    setHistory([]);
    setHistoryError('');
    setExpandedVisit(null);
    setPendingRemoval(null);
  };

  /* --------------------------------------------------------------- mutations */

  const runMutation = async (action, successMessage) => {
    if (!client) return;
    setBusy(true);
    try {
      await action();
      await loadHistory(client.email, true);
      refreshClient(client.email);
      notify('success', successMessage);
    } catch (err) {
      notify('error', err.message || 'That did not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    const { kind, id, name } = pendingRemoval;
    setPendingRemoval(null);
    await runMutation(
      () =>
        kind === 'treatment'
          ? supabaseService.removeBookingTreatment(id)
          : supabaseService.removeBookingProduct(id),
      `${name} removed.`
    );
  };

  /* ----------------------------------------------------------------- pickers */

  const openPicker = (kind, visit) => {
    setPickerQuery('');
    setPickerProduct(null);
    setPickerQty(1);
    setPickerConfirm(null);
    setPicker({ kind, visit, loading: true, checking: false, error: '' });
    ensureCatalog()
      .then(() => setPicker((p) => (p ? { ...p, loading: false } : p)))
      .catch((err) =>
        setPicker((p) => (p ? { ...p, loading: false, error: err.message || 'Could not load the list.' } : p))
      );
  };

  const closePicker = () => {
    setPicker(null);
    setPickerProduct(null);
    setPickerQuery('');
    setPickerConfirm(null);
  };

  const commitTreatment = async (treatment, bookingId) => {
    closePicker();
    await runMutation(
      () => supabaseService.addBookingTreatment(bookingId, treatment),
      `${treatment.name} added.`
    );
  };

  const addTreatment = async (treatment) => {
    const visit = picker.visit;
    // Past visits are pure record-keeping — nothing downstream depends on them.
    if (!visit.date || !visit.time || visit.date < toISODate(new Date())) {
      await commitTreatment(treatment, visit.id);
      return;
    }

    setPicker((p) => (p ? { ...p, checking: true } : p));
    let warning;
    try {
      const dayBookings = await supabaseService.getBookingsForPeriod(visit.date, visit.date);
      warning = overlapWarning(visit, treatment, dayBookings);
    } catch {
      warning = `I could not check your other bookings on ${formatDate(visit.date)} for a clash.`;
    }
    setPicker((p) => (p ? { ...p, checking: false } : p));

    if (warning) {
      setPickerConfirm({ treatment, message: warning });
      return;
    }
    await commitTreatment(treatment, visit.id);
  };

  const addProduct = async () => {
    if (!pickerProduct) return;
    const bookingId = picker.visit.id;
    const product = pickerProduct;
    const qty = pickerQty;
    closePicker();
    await runMutation(
      () => supabaseService.addBookingProduct(bookingId, product, qty),
      `${qty > 1 ? `${qty} × ` : ''}${product.name} added.`
    );
  };

  /* ------------------------------------------------------------------ rebook */

  const openRebook = async (visit) => {
    setRebookVisit(visit);
    setRebookLoading(true);
    setRebookChosenTreatments([]);
    setRebookEditingTreatments(false);
    setRebookDropped([]);
    setRebookSlots([]);
    setRebookSlotsState('idle');
    setRebookSlot(null);
    setRebookPageStart(toISODate(new Date()));
    setRebookOpenDay('');
    setRebookNotes('');
    // Always back on. The common case is somebody standing in front of her, and
    // a sticky "off" would quietly mislabel every rebook after a phone booking.
    setRebookInPerson(true);
    try {
      const data = await ensureCatalog();
      const all = data.treatments.all || [];
      const chosen = [];
      const dropped = [];
      // rebookClient books by treatment id, so each past line has to be matched
      // back to a live treatment. Old bookings may reference treatments that
      // have since been renamed or retired.
      (visit.treatments || []).forEach((line) => {
        const match =
          all.find((t) => t.id === line.treatment_id) ||
          all.find((t) => (t.name || '').toLowerCase() === (line.name || '').toLowerCase());
        if (match) {
          if (!chosen.some((t) => t.id === match.id)) chosen.push(match);
        } else if (line.name) {
          dropped.push(line.name);
        }
      });
      setRebookChosenTreatments(chosen);
      setRebookDropped(dropped);
      // Nothing survived to confirm, so there is nothing to collapse: open the
      // picker rather than showing her an empty "same as last time".
      setRebookEditingTreatments(chosen.length === 0);
    } catch (err) {
      notify('error', err.message || 'Could not load your treatment list.');
      setRebookVisit(null);
    } finally {
      setRebookLoading(false);
    }
  };

  const closeRebook = () => {
    setRebookVisit(null);
    setRebookSlot(null);
    setRebookSlots([]);
    setRebookSlotsState('idle');
  };

  const toggleRebookTreatment = (treatment) => {
    setRebookSlot(null);
    setRebookChosenTreatments((prev) =>
      prev.some((t) => t.id === treatment.id)
        ? prev.filter((t) => t.id !== treatment.id)
        : [...prev, treatment]
    );
  };

  const rebookOpen = rebookVisit !== null;
  const rebookCount = rebookChosenTreatments.length;
  const rebookDuration = rebookChosenTreatments.reduce((sum, t) => sum + (Number(t.duration_minutes) || 0), 0);
  // MAX, not sum: two demanding treatments still need one rest at the end.
  // A scalar so the slot effect does not depend on an array rebuilt every render.
  const rebookRest = rebookChosenTreatments.reduce((max, t) => Math.max(max, Number(t.rest_minutes) || 0), 0);
  const rebookMissingDuration = rebookChosenTreatments.some((t) => !Number(t.duration_minutes));

  useEffect(() => {
    if (!rebookOpen) return undefined;
    if (rebookCount === 0 || rebookMissingDuration || rebookDuration <= 0) {
      setRebookSlots([]);
      setRebookSlotsState('idle');
      return undefined;
    }
    let cancelled = false;
    setRebookSlotsState('loading');
    setRebookSlotsError('');
    // getAdminSlots, not getAvailableSlots: she is allowed to book inside the
    // notice window the public form is held to.
    supabaseService
      .getAdminSlots({
        startDate: rebookPageStart,
        endDate: toISODate(addDays(parseDate(rebookPageStart) || new Date(), REBOOK_DAYS_PER_PAGE - 1)),
        durationMinutes: rebookDuration,
        // MAX across the chosen treatments — one rest at the end, not one each.
        restMinutes: rebookRest,
      })
      .then((rows) => {
        if (cancelled) return;
        const slots = rows || [];
        setRebookSlots(slots);
        setRebookSlotsState('ready');
        setRebookOpenDay(slots.length ? slots[0].slot_date : '');
      })
      .catch((err) => {
        if (cancelled) return;
        setRebookSlots([]);
        setRebookSlotsError(err.message || 'Could not load available times.');
        setRebookSlotsState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [rebookOpen, rebookCount, rebookDuration, rebookRest, rebookMissingDuration, rebookPageStart, slotsReloadKey]);

  const shiftRebookPage = (deltaDays) => {
    const today = toISODate(new Date());
    const next = toISODate(addDays(parseDate(rebookPageStart) || new Date(), deltaDays));
    setRebookPageStart(next < today ? today : next);
    setRebookOpenDay('');
  };

  const rebookAtStart = rebookPageStart === toISODate(new Date());

  const slotsByDay = useMemo(() => {
    const map = new Map();
    rebookSlots.forEach((slot) => {
      if (!map.has(slot.slot_date)) map.set(slot.slot_date, []);
      map.get(slot.slot_date).push(slot);
    });
    return Array.from(map.entries());
  }, [rebookSlots]);

  const submitRebook = async () => {
    if (!rebookSlot || !client) return;
    setRebookSubmitting(true);
    const when = `${formatDate(rebookSlot.slot_date)} at ${formatTime(rebookSlot.slot_time)}`;
    const what = rebookInPerson ? `Rebooked for ${when}` : `Booked for ${when} (phone or text)`;
    try {
      const result = await supabaseService.rebookClient({
        name: client.name || realEmail(client.email) || 'Client',
        // null for a walk-in she booked in person. The identity key is not an
        // address and must never be stored as one.
        email: realEmail(client.email),
        phone: client.phone || null,
        date: rebookSlot.slot_date,
        time: rebookSlot.slot_time,
        treatmentIds: rebookChosenTreatments.map((t) => t.id),
        notes: rebookNotes.trim() || null,
        source: rebookInPerson ? 'admin_rebook' : 'admin_manual',
        treatmentNames: rebookChosenTreatments.map((t) => t.name).join(', '),
        totalDuration: rebookDuration || null,
        totalPrice: treatmentsPriceLabel(rebookChosenTreatments),
        clientType: 'returning',
      });

      // The booking exists either way. Saying nothing about a confirmation that
      // never went is the failure she would not catch until the client no-shows.
      if (result.notified && result.calendarEventId) {
        notify('success', `${what} — confirmation sent and added to your calendar.`);
      } else if (result.calendarEventId) {
        notify('warning', `${what} and added to your calendar, but the confirmation email may not have arrived. Message them directly.`);
      } else if (result.notified) {
        notify('warning', `${what} and the client has been emailed, but it did not reach your Google Calendar. Add it by hand.`);
      } else {
        notify('warning', `${what}, but no confirmation email went out and it is not in your calendar. Check both.`);
      }

      closeRebook();
      await loadHistory(client.email, true);
      refreshClient(client.email);
    } catch (err) {
      if (err.slotTaken) {
        setRebookSlot(null);
        setSlotsReloadKey((k) => k + 1);
        notify('warning', 'That time has just gone. I have reloaded what is still free.');
      } else {
        notify('error', err.message || 'Could not create the booking.');
      }
    } finally {
      setRebookSubmitting(false);
    }
  };

  /* -------------------------------------------------------------- render bits */

  const renderRemoveConfirm = (label) => (
    <div className="cp-confirm">
      <span className="cp-confirm-text">Remove {label}?</span>
      <div className="cp-confirm-actions">
        <button type="button" className="cp-btn-danger" onClick={confirmRemoval} disabled={busy}>
          Remove
        </button>
        <button type="button" className="cp-btn-quiet" onClick={() => setPendingRemoval(null)}>
          Keep
        </button>
      </div>
    </div>
  );

  const isRemoving = (kind, id) =>
    pendingRemoval && pendingRemoval.kind === kind && pendingRemoval.id === id;

  const renderVisit = (visit) => {
    const status = statusMeta(visit.status, visit.date);
    const open = expandedVisit === visit.id;
    const treatments = visit.treatments || [];
    const products = visit.products || [];
    const money = visitMoney(visit);

    return (
      <div key={visit.id} className={`cp-visit ${open ? 'open' : ''} ${status.cls}`}>
        <button
          type="button"
          className="cp-visit-head"
          onClick={() => setExpandedVisit(open ? null : visit.id)}
          aria-expanded={open}
        >
          <span className="cp-visit-head-main">
            <span className="cp-visit-date">{formatDate(visit.date)}</span>
            <span className="cp-visit-sub">
              {visit.time ? formatTime(visit.time) : 'Time not set'}
              {formatDuration(visit.total_duration) ? ` · ${formatDuration(visit.total_duration)}` : ''}
            </span>
          </span>
          <span className="cp-visit-head-right">
            <span className={`cp-visit-total ${money.poa ? 'poa' : ''}`}>{money.totalLabel}</span>
            <span className={`status-pill ${status.cls}`}>{status.label}</span>
          </span>
          {open ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
        </button>

        {open && (
          <div className="cp-visit-body">
            <section className="cp-block">
              <div className="cp-block-head">
                <h4>
                  <Sparkles size={15} aria-hidden="true" /> Treatments
                </h4>
                <button
                  type="button"
                  className="btn-add-small"
                  onClick={() => openPicker('treatment', visit)}
                  disabled={busy}
                >
                  + Add
                </button>
              </div>
              {treatments.length === 0 ? (
                <p className="cp-block-empty">No treatments recorded for this visit.</p>
              ) : (
                <ul className="cp-lines">
                  {treatments.map((t) => (
                    <li key={t.id} className="cp-line">
                      {isRemoving('treatment', t.id) ? (
                        renderRemoveConfirm(t.name)
                      ) : (
                        <>
                          <span className="cp-line-main">
                            <span className="cp-line-name">{t.name}</span>
                            {formatDuration(t.duration_minutes) && (
                              <span className="cp-line-meta">{formatDuration(t.duration_minutes)}</span>
                            )}
                          </span>
                          <span className="cp-line-price">{formatPrice(t.price)}</span>
                          <button
                            type="button"
                            className="cp-remove"
                            aria-label={`Remove ${t.name}`}
                            disabled={busy}
                            onClick={() =>
                              setPendingRemoval({ kind: 'treatment', id: t.id, name: t.name })
                            }
                          >
                            <X size={16} aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="cp-block">
              <div className="cp-block-head">
                <h4>
                  <ShoppingBag size={15} aria-hidden="true" /> Products taken home
                </h4>
                <button
                  type="button"
                  className="btn-add-small"
                  onClick={() => openPicker('product', visit)}
                  disabled={busy}
                >
                  + Add
                </button>
              </div>
              {products.length === 0 ? (
                <p className="cp-block-empty">Nothing bought on this visit.</p>
              ) : (
                <ul className="cp-lines">
                  {products.map((p) => {
                    const qty = Number(p.quantity) || 1;
                    const unit = Number.isFinite(Number(p.price_cents)) ? Number(p.price_cents) : priceToCents(p.price);
                    return (
                      <li key={p.id} className="cp-line">
                        {isRemoving('product', p.id) ? (
                          renderRemoveConfirm(p.name)
                        ) : (
                          <>
                            <span className="cp-line-main">
                              <span className="cp-line-name">{p.name}</span>
                              {qty > 1 && unit !== null && (
                                <span className="cp-line-meta">
                                  {qty} × {formatMoney(unit)}
                                </span>
                              )}
                            </span>
                            <span className="cp-line-price">
                              {unit === null ? formatPrice(p.price) : formatMoney(unit * qty)}
                            </span>
                            <button
                              type="button"
                              className="cp-remove"
                              aria-label={`Remove ${p.name}`}
                              disabled={busy}
                              onClick={() => setPendingRemoval({ kind: 'product', id: p.id, name: p.name })}
                            >
                              <X size={16} aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <div className="cp-totals">
              <div className="cp-total-row">
                <span>Treatments</span>
                <span>{money.treatmentsLabel}</span>
              </div>
              {money.products > 0 && (
                <div className="cp-total-row">
                  <span>Products</span>
                  <span>{formatMoney(money.products)}</span>
                </div>
              )}
              <div className="cp-total-row grand">
                <span>Visit total</span>
                <span>{money.totalLabel}</span>
              </div>
              {money.poa && (
                <p className="cp-total-note">Priced on consultation — this visit has no set price on file.</p>
              )}
            </div>

            {(visit.notes || visit.admin_notes) && (
              <section className="cp-block">
                <div className="cp-block-head">
                  <h4>
                    <StickyNote size={15} aria-hidden="true" /> Notes
                  </h4>
                </div>
                {visit.notes && (
                  <p className="cp-note">
                    <span className="cp-note-tag">From client</span>
                    {visit.notes}
                  </p>
                )}
                {visit.admin_notes && (
                  <p className="cp-note">
                    <span className="cp-note-tag">Yours</span>
                    {visit.admin_notes}
                  </p>
                )}
              </section>
            )}

            <div className="cp-visit-meta">
              {visit.booking_source && (
                <span>{SOURCE_LABELS[visit.booking_source] || visit.booking_source}</span>
              )}
            </div>

            <button type="button" className="cp-btn-primary" onClick={() => openRebook(visit)}>
              <RefreshCw size={16} aria-hidden="true" /> Rebook this
            </button>
          </div>
        )}
      </div>
    );
  };

  const hasPoaVisit = history.some((v) => visitMoney(v).poa);

  /* ------------------------------------------------------------------ list */

  if (!client) {
    return (
      <div className="clients-panel">
        <div className="cp-search">
          <Search size={18} className="cp-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="cp-search-input"
            placeholder="Search name, email or phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search clients"
          />
          {query && (
            <button type="button" className="cp-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>

        {listError && (
          <div className="cp-alert error">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{listError}</span>
          </div>
        )}

        {listLoading && <div className="cp-loading">Loading clients…</div>}

        {!listLoading && !listError && clients.length === 0 && (
          <div className="no-bookings">
            <Search size={40} className="cp-empty-icon" aria-hidden="true" />
            <h3>{query.trim() ? 'No one matches that' : 'No clients yet'}</h3>
            <p>
              {query.trim()
                ? 'Try part of a name, an email address or a phone number.'
                : 'Clients appear here once they have a booking.'}
            </p>
          </div>
        )}

        {!listLoading && clients.length > 0 && (
          <>
            {!query.trim() && <p className="cp-list-hint">Your {clients.length} most recent clients</p>}
            <ul className="cp-client-list">
              {clients.map((row) => (
                <li key={row.email || row.user_id || row.name}>
                  <button type="button" className="cp-client-row" onClick={() => openClient(row)}>
                    <span className="cp-client-main">
                      <span className="cp-client-name">{row.name || row.email}</span>
                      <span className="cp-client-contact">{row.phone || row.email}</span>
                    </span>
                    <span className="cp-client-stats">
                      <span className="cp-client-spend">{formatMoney(row.lifetime_cents)}</span>
                      <span className="cp-client-meta">{clientSummaryLine(row)}</span>
                    </span>
                    <ChevronRight size={18} className="cp-client-chevron" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- detail */

  return (
    <div className="clients-panel">
      <button type="button" className="cp-back" onClick={closeClient}>
        <ArrowLeft size={16} aria-hidden="true" /> All clients
      </button>

      <div className="cp-client-head">
        <div className="cp-client-head-top">
          <h2>{client.name || realEmail(client.email) || 'Client'}</h2>
          {client.user_id && (
            <span className="cp-badge">
              <UserCheck size={13} aria-hidden="true" /> Has an account
            </span>
          )}
        </div>

        <div className="cp-contact-links">
          {client.phone && (
            <a href={`tel:${client.phone}`}>
              <Phone size={14} aria-hidden="true" /> {client.phone}
            </a>
          )}
          {client.email && isNameKey(client.email) && (
            <span className="cp-no-email">
              <Mail size={14} aria-hidden="true" /> No email on file
            </span>
          )}

          {client.email && !isNameKey(client.email) && (
            <a href={`mailto:${client.email}`}>
              <Mail size={14} aria-hidden="true" /> {client.email}
            </a>
          )}
        </div>

        {/* Only shown for clients with an account: a guest has no profile row to
            hold the flag, and no saved birthday to trigger the email anyway. */}
        {client.user_id && (
          <div className="cp-optout">
            <label className="cp-points-toggle">
              <input
                type="checkbox"
                checked={optOut}
                disabled={optOutBusy}
                onChange={(e) => toggleOptOut(e.target.checked)}
              />
              <span className="cp-points-text">
                <span className="cp-points-label">No birthday emails</span>
                <span className="cp-points-hint">
                  Turn on if they’ve asked not to get the yearly birthday offer. Their
                  appointment confirmations and reminders keep sending either way.
                </span>
              </span>
            </label>
            {optOutError && <p className="cp-optout-error">{optOutError}</p>}
          </div>
        )}

        <div className="cp-stat-grid">
          <div className="cp-stat">
            <span className="cp-stat-label">Completed visits</span>
            <span className="cp-stat-value">{Number(client.visits) || 0}</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-label">Lifetime{hasPoaVisit ? ' *' : ''}</span>
            <span className="cp-stat-value">{formatMoney(client.lifetime_cents)}</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-label">First visit</span>
            <span className="cp-stat-value small">{formatDateShort(client.first_visit)}</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-label">Last visit</span>
            <span className="cp-stat-value small">{formatDateShort(client.last_visit)}</span>
          </div>
        </div>

        {/* lifetime_cents sums stored cents, which a POA visit has none of.
            Understating what a client has spent without saying so is worse than
            admitting the gap. */}
        {hasPoaVisit && (
          <p className="cp-stat-note">* Excludes consultation-priced visits, which have no set price on file.</p>
        )}
      </div>

      {historyLoading && <div className="cp-loading">Loading visits…</div>}

      {!historyLoading && historyError && (
        <div className="cp-alert error">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{historyError}</span>
          <button type="button" className="cp-btn-quiet" onClick={() => loadHistory(client.email)}>
            Try again
          </button>
        </div>
      )}

      {!historyLoading && !historyError && history.length === 0 && (
        <div className="no-bookings">
          <CalendarClock size={40} className="cp-empty-icon" aria-hidden="true" />
          <h3>No visits recorded</h3>
          <p>Once {client.name || 'this client'} has a booking it will show here.</p>
        </div>
      )}

      {!historyLoading && history.length > 0 && (
        <>
          <div className="cp-visits-head">
            {/* total_bookings counts every status, so this heading always agrees
                with the cards underneath it. */}
            <h3>
              {Number(client.total_bookings) || history.length} booking
              {(Number(client.total_bookings) || history.length) === 1 ? '' : 's'} on file
            </h3>
            {!Number(client.visits) && <span>none completed yet</span>}
          </div>
          <div className="cp-visits">{history.map(renderVisit)}</div>
        </>
      )}

      {/* ------------------------------------------------------- picker modal */}
      {picker && (
        <div className="modal-overlay cp-overlay" onClick={closePicker}>
          <div className="modal-content cp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{picker.kind === 'treatment' ? 'Add a treatment' : 'Add a product'}</h2>
              <button type="button" className="modal-close" onClick={closePicker} aria-label="Close">
                ×
              </button>
            </div>

            <div className="modal-body cp-modal-body">
              {picker.loading && <div className="cp-loading">Loading…</div>}

              {picker.checking && <div className="cp-loading">Checking that day for clashes…</div>}

              {picker.error && (
                <div className="cp-alert error">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>{picker.error}</span>
                </div>
              )}

              {/* Never blocks the change — she may have genuinely run over and is
                  recording what happened. It just must not be a surprise. */}
              {pickerConfirm && (
                <div className="cp-overlap">
                  <div className="cp-alert warn">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>{pickerConfirm.message}</span>
                  </div>
                  <p className="cp-overlap-note">
                    Nothing has been changed yet, and adding it will not move the other booking.
                  </p>
                  <div className="cp-overlap-actions">
                    <button
                      type="button"
                      className="cp-btn-primary"
                      disabled={busy}
                      onClick={() => commitTreatment(pickerConfirm.treatment, picker.visit.id)}
                    >
                      Add it anyway
                    </button>
                    <button type="button" className="cp-btn-quiet wide" onClick={() => setPickerConfirm(null)}>
                      Back to the list
                    </button>
                  </div>
                </div>
              )}

              {!picker.loading && !picker.error && !picker.checking && !pickerConfirm && (
                <>
                  <div className="cp-search compact">
                    <Search size={16} className="cp-search-icon" aria-hidden="true" />
                    <input
                      type="search"
                      className="cp-search-input"
                      placeholder={picker.kind === 'treatment' ? 'Filter treatments' : 'Filter products'}
                      value={pickerQuery}
                      onChange={(e) => setPickerQuery(e.target.value)}
                      aria-label="Filter list"
                    />
                  </div>

                  {picker.kind === 'treatment' ? (
                    (() => {
                      const needle = pickerQuery.trim().toLowerCase();
                      const groups = Object.entries(catalog.treatments.grouped || {})
                        .map(([category, items]) => [
                          category,
                          items.filter((t) => !needle || (t.name || '').toLowerCase().includes(needle)),
                        ])
                        .filter(([, items]) => items.length > 0);

                      if (groups.length === 0) {
                        return <p className="cp-block-empty">No treatments match that.</p>;
                      }

                      return groups.map(([category, items]) => (
                        <div key={category} className="cp-pick-group">
                          <p className="cp-pick-group-title">{category}</p>
                          {items.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              className="cp-pick-row"
                              onClick={() => addTreatment(t)}
                              disabled={busy}
                            >
                              <span className="cp-line-main">
                                <span className="cp-line-name">{t.name}</span>
                                {formatDuration(t.duration_minutes) && (
                                  <span className="cp-line-meta">{formatDuration(t.duration_minutes)}</span>
                                )}
                              </span>
                              <span className="cp-line-price">{formatPrice(t.price)}</span>
                              <Plus size={16} aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      ));
                    })()
                  ) : (
                    (() => {
                      const needle = pickerQuery.trim().toLowerCase();
                      const items = (catalog.products || []).filter(
                        (p) => !needle || (p.name || '').toLowerCase().includes(needle)
                      );
                      if (items.length === 0) {
                        return <p className="cp-block-empty">No products match that.</p>;
                      }
                      return items.map((p) => {
                        const chosen = pickerProduct && pickerProduct.id === p.id;
                        // stock null means untracked — say nothing rather than "0".
                        // It can now go negative, and "-1 in stock" is the truth.
                        const tracked = p.stock !== null && p.stock !== undefined;
                        const stock = Number(p.stock);
                        const meta = [p.category || 'Product'];
                        if (p.is_active === false) meta.push('hidden in shop');
                        if (tracked && stock > 0) meta.push(`${stock} in stock`);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`cp-pick-row ${chosen ? 'chosen' : ''}`}
                            onClick={() => {
                              setPickerProduct(p);
                              setPickerQty(1);
                            }}
                          >
                            <span className="cp-line-main">
                              <span className="cp-line-name">
                                {p.name}
                                {/* Never blocks the sale — she may be selling the
                                    last one out of her bag. */}
                                {tracked && stock === 0 && <span className="cp-flag">None left</span>}
                                {tracked && stock < 0 && <span className="cp-flag">{stock} in stock</span>}
                              </span>
                              <span className="cp-line-meta">{meta.join(' · ')}</span>
                            </span>
                            <span className="cp-line-price">{formatPrice(p.price)}</span>
                          </button>
                        );
                      });
                    })()
                  )}
                </>
              )}
            </div>

            {picker.kind === 'product' && pickerProduct && (
              <div className="cp-modal-foot column">
                {pickerProduct.stock !== null &&
                  pickerProduct.stock !== undefined &&
                  pickerQty > Number(pickerProduct.stock) && (
                    <p className="cp-foot-warn">
                      {Number(pickerProduct.stock) > 0
                        ? `Your list says ${Number(pickerProduct.stock)} left.`
                        : Number(pickerProduct.stock) === 0
                          ? 'Your list says none left.'
                          : `Your list is already ${Math.abs(Number(pickerProduct.stock))} short.`}{' '}
                      Recording the sale is fine — the count just goes further below zero until you restock.
                    </p>
                  )}
                <div className="cp-foot-row">
                  <div className="cp-qty">
                    <button
                      type="button"
                      onClick={() => setPickerQty((q) => Math.max(1, q - 1))}
                      aria-label="Fewer"
                      disabled={pickerQty <= 1}
                    >
                      <Minus size={16} aria-hidden="true" />
                    </button>
                    <span className="cp-qty-value" aria-live="polite">
                      {pickerQty}
                    </span>
                    <button type="button" onClick={() => setPickerQty((q) => Math.min(99, q + 1))} aria-label="More">
                      <Plus size={16} aria-hidden="true" />
                    </button>
                  </div>
                  <button type="button" className="cp-btn-primary" onClick={addProduct} disabled={busy}>
                    Add {pickerProduct.name}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- rebook modal */}
      {rebookOpen && (
        <div className="modal-overlay cp-overlay" onClick={closeRebook}>
          <div className="modal-content cp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rebook {client.name || realEmail(client.email) || 'Client'}</h2>
              <button type="button" className="modal-close" onClick={closeRebook} aria-label="Close">
                ×
              </button>
            </div>

            <div className="modal-body cp-modal-body">
              {rebookLoading && <div className="cp-loading">Loading treatments…</div>}

              {!rebookLoading && (
                <>
                  {rebookDropped.length > 0 && (
                    <div className="cp-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        {rebookDropped.join(', ')} {rebookDropped.length === 1 ? 'is' : 'are'} no longer on your
                        price list, so {rebookDropped.length === 1 ? 'it has' : 'they have'} not been carried over.
                      </span>
                    </div>
                  )}

                  <h3 className="cp-modal-section">What is she having?</h3>

                  {/* Collapsed by default. The overwhelmingly common rebook is the
                      same treatment again, and scrolling the whole price list to
                      agree with something already known is the slow path. */}
                  {!rebookEditingTreatments ? (
                    <div className="cp-same-again">
                      <span className="cp-same-again-label">Same as last time</span>
                      {rebookCount > 0 ? (
                        <>
                          <span className="cp-same-again-list">
                            {rebookChosenTreatments.map((t) => t.name).join(' · ')}
                          </span>
                          <span className="cp-same-again-meta">
                            {rebookMissingDuration ? 'duration not set' : formatDuration(rebookDuration)} ·{' '}
                            {treatmentsPriceLabel(rebookChosenTreatments)}
                          </span>
                        </>
                      ) : (
                        <span className="cp-same-again-list">
                          Nothing chosen yet — pick what she is having.
                        </span>
                      )}
                      <button
                        type="button"
                        className="cp-btn-quiet wide"
                        onClick={() => setRebookEditingTreatments(true)}
                      >
                        <Plus size={15} aria-hidden="true" /> Change treatments
                      </button>
                    </div>
                  ) : (
                    <>
                      {Object.entries(catalog.treatments.grouped || {}).map(([category, items]) => (
                        <div key={category} className="cp-pick-group">
                          <p className="cp-pick-group-title">{category}</p>
                          {items.map((t) => {
                            const checked = rebookChosenTreatments.some((s) => s.id === t.id);
                            return (
                              <label key={t.id} className={`cp-check-row ${checked ? 'checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleRebookTreatment(t)}
                                />
                                <span className="cp-line-main">
                                  <span className="cp-line-name">{t.name}</span>
                                  <span className="cp-line-meta">
                                    {formatDuration(t.duration_minutes) || 'no duration set'}
                                  </span>
                                </span>
                                <span className="cp-line-price">{formatPrice(t.price)}</span>
                              </label>
                            );
                          })}
                        </div>
                      ))}

                      {rebookCount > 0 && (
                        <div className="cp-summary-bar">
                          <span>
                            {rebookCount} treatment{rebookCount === 1 ? '' : 's'}
                          </span>
                          <span>
                            {rebookMissingDuration ? 'duration unknown' : formatDuration(rebookDuration)}
                          </span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="cp-btn-quiet wide"
                        onClick={() => setRebookEditingTreatments(false)}
                        disabled={rebookCount === 0}
                      >
                        Done choosing
                      </button>
                    </>
                  )}

                  <h3 className="cp-modal-section">Pick a time</h3>

                  {rebookCount === 0 && (
                    <p className="cp-block-empty">Choose at least one treatment to see what is free.</p>
                  )}

                  {rebookCount > 0 && rebookMissingDuration && (
                    <div className="cp-alert warn">
                      <AlertTriangle size={16} aria-hidden="true" />
                      <span>
                        One of the treatments you picked has no length set, so I cannot work out which times fit.
                        Either unpick it, or add a duration to it in your price list first.
                      </span>
                    </div>
                  )}

                  {/* A fortnight at a time with arrows, one card per day, times in a
                      grid — the same shape as the public booking form. */}
                  {rebookCount > 0 && !rebookMissingDuration && (
                    <>
                      <div className="cp-slot-nav">
                        <button
                          type="button"
                          className="cp-slot-nav-btn"
                          onClick={() => shiftRebookPage(-REBOOK_DAYS_PER_PAGE)}
                          disabled={rebookAtStart || rebookSlotsState === 'loading'}
                          aria-label="Earlier dates"
                        >
                          <ChevronLeft size={18} aria-hidden="true" />
                        </button>
                        <span className="cp-slot-nav-label">
                          {rebookAtStart ? 'Next 2 weeks' : `From ${formatDayLabel(rebookPageStart)}`}
                        </span>
                        <button
                          type="button"
                          className="cp-slot-nav-btn"
                          onClick={() => shiftRebookPage(REBOOK_DAYS_PER_PAGE)}
                          disabled={rebookSlotsState === 'loading'}
                          aria-label="Later dates"
                        >
                          <ChevronRight size={18} aria-hidden="true" />
                        </button>
                      </div>

                      {rebookSlotsState === 'loading' && <div className="cp-loading">Finding times…</div>}

                      {rebookSlotsState === 'error' && (
                        <div className="cp-alert error">
                          <AlertTriangle size={16} aria-hidden="true" />
                          <span>{rebookSlotsError}</span>
                          <button
                            type="button"
                            className="cp-btn-quiet"
                            onClick={() => setSlotsReloadKey((k) => k + 1)}
                          >
                            Try again
                          </button>
                        </div>
                      )}

                      {rebookSlotsState === 'ready' && slotsByDay.length === 0 && (
                        <p className="cp-block-empty">
                          Nothing free in this fortnight for a {formatDuration(rebookDuration)} appointment.
                          Use the arrow above to look further ahead.
                        </p>
                      )}

                      {rebookSlotsState === 'ready' &&
                        slotsByDay.map(([day, slots]) => {
                          // With only a couple of days free there is nothing to
                          // choose between, so they start open — as on the booking form.
                          const dayOpen = rebookOpenDay === day || slotsByDay.length <= 3;
                          const dayPlaces = Array.from(
                            new Set(slots.map((s) => s.location_name).filter(Boolean))
                          );
                          return (
                            <div key={day} className="cp-day">
                              <button
                                type="button"
                                className={`cp-day-head ${dayOpen ? 'open' : ''}`}
                                onClick={() =>
                                  setRebookOpenDay(dayOpen && rebookOpenDay === day ? '' : day)
                                }
                                aria-expanded={dayOpen}
                              >
                                <span>{formatDayLabel(day)}</span>
                                <span className="cp-day-count">
                                  {slots.length} time{slots.length === 1 ? '' : 's'}
                                </span>
                                {dayOpen ? (
                                  <ChevronUp size={16} aria-hidden="true" />
                                ) : (
                                  <ChevronDown size={16} aria-hidden="true" />
                                )}
                              </button>
                              {dayOpen && (
                                <>
                                  {/* Said once for the day, not stamped on every time */}
                                  {dayPlaces.length === 1 && (
                                    <p className="cp-day-location">
                                      {slots[0].location_address || dayPlaces[0]}
                                    </p>
                                  )}
                                  <div className="cp-slot-grid">
                                    {slots.map((slot) => {
                                      const active =
                                        rebookSlot &&
                                        rebookSlot.slot_date === slot.slot_date &&
                                        rebookSlot.slot_time === slot.slot_time;
                                      return (
                                        <button
                                          key={`${slot.slot_date}-${slot.slot_time}-${slot.location_name || ''}`}
                                          type="button"
                                          className={`cp-slot ${active ? 'active' : ''}`}
                                          onClick={() => setRebookSlot(slot)}
                                        >
                                          <span>{formatTime(slot.slot_time)}</span>
                                          {/* only when one day genuinely runs in two places */}
                                          {dayPlaces.length > 1 && slot.location_name && (
                                            <span className="cp-slot-loc">{slot.location_name}</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                    </>
                  )}

                  <h3 className="cp-modal-section">Notes</h3>
                  <textarea
                    className="reschedule-textarea cp-notes"
                    rows="3"
                    value={rebookNotes}
                    onChange={(e) => setRebookNotes(e.target.value)}
                    placeholder="Anything to remember for this appointment…"
                    aria-label="Booking notes"
                  />
                </>
              )}
            </div>

            <div className="cp-modal-foot column">
              <label className="cp-points-toggle">
                <input
                  type="checkbox"
                  checked={rebookInPerson}
                  onChange={(e) => setRebookInPerson(e.target.checked)}
                />
                <span className="cp-points-text">
                  <span className="cp-points-label">Rebooked in person</span>
                  <span className="cp-points-hint">
                    On for rebooking with the client in front of you. Turn off for bookings
                    taken by phone or text.
                  </span>
                </span>
              </label>

              {rebookSlot && (
                <p className="cp-chosen-slot">
                  <span className="cp-chosen-slot-label">Chosen</span>
                  {formatDayLabel(rebookSlot.slot_date)} at {formatTime(rebookSlot.slot_time)}
                  {rebookSlot.location_name ? ` · ${rebookSlot.location_name}` : ''}
                </p>
              )}
              {/* One row, not two: the footer does not scroll, so every row of it
                  is a row taken off the list of times above it. */}
              <div className="cp-foot-actions">
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={submitRebook}
                  disabled={!rebookSlot || rebookSubmitting || rebookCount === 0}
                >
                  {rebookSubmitting ? 'Booking…' : 'Confirm booking'}
                </button>
                <button type="button" className="cp-btn-quiet wide" onClick={closeRebook}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPanel;
